import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { WORKLOG_NOTE_MAX_CHARS } from '../constants';
import type { TimerSession } from '../types';
import { TimerElapsed } from './TimerElapsed';

/** Stopping this timer needs a note — no Skip (BE-ADR-109). The timer's own org
 *  decides (the API says so on the timer); the active org is only a fallback for
 *  an older API without `note_required`. */
function useTimerNoteRequired(timer: TimerSession): boolean {
  const activeRequires = useSession(
    s => s.organizations.find(o => o.slug === s.activeOrgSlug)?.timer_note_mode === 'required'
  );
  return timer.note_required ?? activeRequires;
}

interface TimerNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The running timer being stopped. */
  timer: TimerSession;
  /** `switch` — stopping it to start a timer on another item. */
  mode?: 'stop' | 'switch';
  pending?: boolean;
  /** A refused stop (e.g. the note's field error) — shown under the note. */
  error?: unknown;
  /** Called with the trimmed note; '' when skipped. */
  onConfirm: (note: string) => void;
}

/**
 * "What did you work on?" — asked when a timer stops (DEV13-117), like an Upwork
 * memo or a Tempo worklog note. Optional orgs get Skip; required ones can't log
 * until there's a note. The note becomes the worklog's description.
 */
export function TimerNoteDialog({ open, onOpenChange, ...form }: TimerNoteDialogProps) {
  const noteRef = useRef<HTMLTextAreaElement>(null);
  // Once Log time / Skip is sent the stop can't be taken back — don't let
  // Cancel / Esc / the ✕ pretend it was.
  const close = (next: boolean) => {
    if (!next && form.pending) return;
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        // One shrinkable column: a long work-item title must wrap inside the
        // dialog, not widen the grid track past it.
        className="grid-cols-[minmax(0,1fr)] sm:max-w-md"
        // Land in the note, not on the first button.
        onOpenAutoFocus={e => {
          e.preventDefault();
          noteRef.current?.focus();
        }}
      >
        {/* Content unmounts on close, so each opening starts from the note
            given at start (usually none). */}
        <TimerNoteForm {...form} noteRef={noteRef} onCancel={() => close(false)} />
      </DialogContent>
    </Dialog>
  );
}

function TimerNoteForm({
  timer,
  mode = 'stop',
  pending = false,
  error,
  onConfirm,
  onCancel,
  noteRef,
}: Omit<TimerNoteDialogProps, 'open' | 'onOpenChange'> & {
  onCancel: () => void;
  noteRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const required = useTimerNoteRequired(timer);
  const [note, setNote] = useState(timer.description);

  const trimmed = note.trim();
  const canLog = !pending && (!required || trimmed.length > 0);
  const fieldErrors = (error as ApiError | null | undefined)?.fieldErrors ?? {};
  const errorText = (fieldErrors.description ?? fieldErrors.previous_description)?.[0];
  const item = timer.work_item_title ?? timer.project_name ?? 'this item';

  function submit() {
    if (canLog) onConfirm(trimmed);
  }

  return (
    <>
      <DialogHeader className="min-w-0 pr-6">
        <DialogTitle>What did you work on?</DialogTitle>
        <DialogDescription className="space-y-1">
          <span className="line-clamp-2 block break-words" title={item}>
            {mode === 'switch' ? `Your timer on ${item} stops and is logged.` : item}
          </span>
          <TimerElapsed startedAt={timer.started_at} className="text-foreground text-xs" />
        </DialogDescription>
      </DialogHeader>

      <form
        className="min-w-0 space-y-2"
        onSubmit={e => {
          e.preventDefault();
          submit();
        }}
      >
        <Label htmlFor="timer-note">
          Note{' '}
          <span className="text-muted-foreground font-normal">
            {required ? '(required)' : '(optional)'}
          </span>
        </Label>
        <Textarea
          id="timer-note"
          ref={noteRef}
          rows={3}
          maxLength={WORKLOG_NOTE_MAX_CHARS}
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => {
            // Enter logs; Shift+Enter is a new line. Not while an IME is
            // composing — that Enter commits the word.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="e.g. Fixed the invoice PDF totals; reviewed the lead-import PR"
          aria-invalid={errorText ? true : undefined}
          aria-describedby="timer-note-help"
        />
        <div
          id="timer-note-help"
          className="text-muted-foreground flex items-start justify-between gap-3 text-xs"
        >
          <span>A short memo for the worklog and timesheet: what you did, not how long.</span>
          <span
            className={cn(
              'shrink-0 tabular-nums',
              note.length >= WORKLOG_NOTE_MAX_CHARS && 'text-foreground font-medium'
            )}
          >
            {note.length}/{WORKLOG_NOTE_MAX_CHARS}
          </span>
        </div>
        {errorText ? (
          <p role="alert" className="text-destructive text-xs">
            {errorText}
          </p>
        ) : null}

        <DialogFooter className="pt-2">
          <Button type="button" variant="ghost" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
          {!required ? (
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => onConfirm('')}
            >
              Skip
            </Button>
          ) : null}
          <Button type="submit" disabled={!canLog}>
            {pending ? 'Logging…' : mode === 'switch' ? 'Log & switch' : 'Log time'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
